import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { User } from "@supabase/supabase-js";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ProfileContent from "@/components/profile/ProfileContent";
import ProfileLoading from "@/components/profile/ProfileLoading";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

// Create our own error boundary component since there are issues with the import
interface Props {
  children?: React.ReactNode;
  fallbackComponent?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Main implementation as class component (required for error boundaries)
class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Profile error boundary caught an error:", error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  public render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallbackComponent;
      return FallbackComponent ? (
        <FallbackComponent 
          error={this.state.error!} 
          resetError={() => this.setState({ hasError: false, error: null })} 
        />
      ) : (
        <div className="p-6 bg-red-50 border border-red-200 rounded-md">
          <h3 className="text-lg font-medium text-red-800 mb-2">Something went wrong</h3>
          <p className="text-red-700 mb-4">{this.state.error?.message || "Unknown error"}</p>
          <Button onClick={() => this.setState({ hasError: false, error: null })}>
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple fallback component for error boundary
const ProfileErrorFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => {
  return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-md">
      <h3 className="text-lg font-medium text-red-800 mb-2">Something went wrong</h3>
      <p className="text-red-700 mb-4">{error.message || "An unknown error occurred"}</p>
      <Button onClick={resetError}>Try Again</Button>
    </div>
  );
};

const Profile = () => {
  const { user, loading } = useAuth();
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Clean up any old token storage when viewing profile page
  useEffect(() => {
    const cleanupTokens = () => {
      console.log("=== Profile: Checking for token cleanup ===");
      try {
        // Check if we have old tokens that need to be cleared
        const sessionToken = sessionStorage.getItem("googleAuthToken");
        const localToken = localStorage.getItem("googleAuthToken");
        
        // If returning to profile after a failed auth flow, clear tokens
        if (sessionToken || localToken) {
          console.log("Cleaning up old Google auth tokens on profile page visit");
          sessionStorage.removeItem("googleAuthToken");
          sessionStorage.removeItem("googleRefreshToken");
          sessionStorage.removeItem("googleAuthBusinessId");
          localStorage.removeItem("googleAuthToken");
          localStorage.removeItem("googleRefreshToken");
          localStorage.removeItem("googleTokenExpires");
          localStorage.removeItem("googleAuthBusinessId");
          
          console.log("Tokens cleaned up successfully");
        }
      } catch (error) {
        console.error("Error cleaning up tokens:", error);
      }
    };
    
    cleanupTokens();
  }, []);

  const handleOpenConnectionModal = () => {
    setIsConnectionModalOpen(true);
  };

  const handleStartGoogleAuth = () => {
    setIsConnectionModalOpen(false);
    setIsRedirecting(true);
    
    toast({
      title: "Starting Google Authorization",
      description: "Redirecting you to connect your Google Business Profile...",
    });
    
    setTimeout(() => {
      setIsRedirecting(false);
      navigate('/business/connect');
    }, 1000);
  };

  const handleErrorReset = () => {
    setErrorMessage(null);
    window.location.reload();
  };

  if (loading || isRedirecting) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <ProfileLoading reason={isRedirecting ? "preparing Google authorization" : "loading your profile"} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <main className="container py-10">
        {errorMessage ? (
          <ProfileErrorFallback error={{ message: errorMessage } as Error} resetError={handleErrorReset} />
        ) : (
          <ErrorBoundary fallbackComponent={ProfileErrorFallback} onError={(error) => setErrorMessage(error.message)}>
            <ProfileContent 
              user={user} 
              onOpenConnectionModal={handleOpenConnectionModal}
            />
          </ErrorBoundary>
        )}
      </main>

      <Dialog open={isConnectionModalOpen} onOpenChange={setIsConnectionModalOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col space-y-4">
            <h2 className="text-lg font-semibold">Connect Google Business Profile</h2>
            <p className="text-sm text-muted-foreground">
              Connect your Google Business Profile to manage your online presence and respond to reviews.
            </p>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setIsConnectionModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStartGoogleAuth}
                className="flex-1"
              >
                Connect
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
